import { keyframes, style } from 'typestyle';
import { NestedCSSProperties } from 'typestyle/lib/types';
import { RightContainerPayloadType } from './RightContainer';
import { isNil } from 'lodash';

export function rightContainer(payloadType: RightContainerPayloadType | undefined) {
  const width = payloadType === RightContainerPayloadType.Agent ? '38rem' : '25rem';

  const openPos = { left: `calc(100% - ${width})`, display: 'block' };
  const closedPos = { left: '101%', display: 'none' };

  const slideIn = keyframes({
    $debugName: 'slideIn',
    from: closedPos,
    to: openPos,
  });

  const slideOut = keyframes({
    $debugName: 'slideOut',
    from: openPos,
    to: closedPos,
  });

  let options: NestedCSSProperties = {
    $debugName: 'rightContainer',
    position: 'absolute',
    right: 0,
    zIndex: 103,
    backgroundColor: 'white',
    height: '100%',
    overflow: 'auto',
    borderLeft: '3px solid #ddd',
    width: width,
    animationDuration: '.4s',
  };

  if (isNil(payloadType)) {
    // no animation, initial component load
    options = {
      ...options,
      ...closedPos,
    };
  } else if (payloadType === RightContainerPayloadType.Collapsing) {
    // animate closed
    options = {
      ...options,
      ...closedPos,
      animationName: slideOut,
    };
  } else {
    // animate open
    options = {
      ...options,
      ...openPos,
      animationName: slideIn,
    };
  }

  return style(options);
}

const stylePane = style({
  $debugName: 'stylePane',
  height: '100%',
  overflow: 'auto',
});

const toggleStateIcon = style({
  $debugName: 'togglePane',
  marginTop: `calc(50vh - 16px)`,
});

const rightContainerCloseIcon = style({
  $debugName: 'rightContainerCloseIcon',
  marginRight: 10,
  $nest: {
    i: {
      cursor: 'pointer',
    },
  },
});

function styleHeaderSpacer(isStyleInfo: boolean, hasPinnedContent: boolean) {
  const base = isStyleInfo ? 85 : 50;
  // pinned content (eg swatches) adds its own row to the fixed header, so the
  // spacer needs to grow with it to keep the scrollable content from sliding underneath
  const pinnedContentAllowance = hasPinnedContent ? 50 : 0;
  return style({
    height: `${base + pinnedContentAllowance}px`,
  });
}

function styleHeaderContainer(
  isStyleInfo: boolean,
  payloadType: RightContainerPayloadType | undefined,
  stylePaneLoading: boolean,
) {
  const width = payloadType === RightContainerPayloadType.Agent ? '36.9rem' : '23.9rem';
  return style({
    $debugName: 'rightContainerActionsContainer',
    width: width,
    position: 'fixed',
    display: 'flex',
    flexWrap: 'wrap',
    height: isStyleInfo ? 'auto' : '50px',
    justifyContent: isStyleInfo ? 'space-between' : 'flex-end',
    background: '#ffffff',
    paddingLeft: '1.1rem',
    paddingTop: '20px',
    zIndex: stylePaneLoading ? 1 : 0,
  });
}

const headerPinnedRow = style({
  $debugName: 'headerPinnedRow',
  flexBasis: '100%',
  paddingRight: '1.1rem',
});

const headerText = style({
  fontWeight: 'bold',
  // edit style button was wrapped when header text was long
  flex: '1 1 0%',
  minWidth: 0,
  $nest: {
    '& .title': {
      fontSize: '20px',
      wordBreak: 'break-all',
    },
    '& .description': {
      fontSize: '12px',
      wordBreak: 'break-all',
    },
  },
});

const headerActions = style({
  display: 'flex',
  alignItems: 'baseline',
  flexShrink: 0,
});

const headerEditButtonSkeleton = style({
  marginRight: 10,
});

export default {
  styleHeaderContainer,
  styleHeaderSpacer,
  headerPinnedRow,
  rightContainer,
  stylePane,
  toggleStateIcon,
  rightContainerCloseIcon,
  headerText,
  headerActions,
  headerEditButtonSkeleton,
};
